import type {
  Locale,
  MessageKey,
  StudentLifecycleEventContract,
  StudentLifecycleStatusContract,
  StudentProfileContract,
} from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Checkbox } from "@chams-pay/design-system/components/checkbox";
import { Block } from "konsta/react";
import { GraduationCap, LogOut, PencilLine, RotateCcw } from "lucide-react";
import type {
  StudentLifecycleController,
  StudentLifecycleMode,
} from "../core/use-student-lifecycle.js";
import { DatePicker } from "../ui/date-picker.js";
import { FilterSelect } from "../ui/filter-select.js";
import { Amount, Bidi, DateValue } from "../ui/figures.js";
import { MobileSurface } from "./surfaces.js";

const modeTitle: Record<StudentLifecycleMode, MessageKey> = {
  withdrawal: "sp.withdrawStudent",
  graduation: "sp.graduateStudent",
  return: "sp.returnStudent",
  correction: "sp.correctLifecycle",
};

const modeHelp: Record<StudentLifecycleMode, MessageKey> = {
  withdrawal: "sp.lifecycleWithdrawalHelp",
  graduation: "sp.lifecycleGraduationHelp",
  return: "sp.lifecycleReturnHelp",
  correction: "sp.lifecycleCorrectionHelp",
};

const statusKey: Record<StudentLifecycleStatusContract, MessageKey> = {
  active: "sp.statusActive",
  withdrawn: "sp.statusWithdrawn",
  graduated: "sp.statusGraduated",
};

const eventKey: Record<StudentLifecycleEventContract["type"], MessageKey> = {
  withdrawal: "sp.lifecycleWithdrawal",
  graduation: "sp.lifecycleGraduation",
  return: "sp.lifecycleReturn",
  correction: "sp.lifecycleCorrected",
};

function failureKey(code: string | null): MessageKey | null {
  if (!code) return null;
  if (code === "PENDING_FUTURE_INSTRUMENT") return "sp.lifecyclePendingBlock";
  if (code === "LIFECYCLE_CORRECTION_CONFLICT") return "sp.lifecycleCorrectionConflict";
  if (code === "GRADUATION_NOT_ALLOWED") return "sp.lifecycleGraduationNotAllowed";
  return "sp.lifecycleFailed";
}

export function MobileStudentLifecycle({
  locale,
  student,
  controller,
  editable,
}: {
  readonly locale: Locale;
  readonly student: StudentProfileContract;
  readonly controller: StudentLifecycleController;
  readonly editable: boolean;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const mode = controller.mode;
  const errorKey = failureKey(controller.failedCode);
  const prepaidReviewMad =
    student.lifecycleStatus === "active"
      ? "0"
      : student.obligations
          .filter(
            (obligation) =>
              obligation.dueDate > student.lifecycle.effectiveDate &&
              BigInt(obligation.settledMad) > 0n,
          )
          .reduce((total, obligation) => total + BigInt(obligation.settledMad), 0n)
          .toString();
  const statusOptions: StudentLifecycleStatusContract[] = student.lifecycle.graduationEligible
    ? ["active", "withdrawn", "graduated"]
    : ["active", "withdrawn"];

  return (
    <>
      <div className="student-section mx-3.5 mt-3.5 overflow-hidden rounded-2xl bg-white shadow-e1">
        <div className="flex items-center gap-2 border-b border-border/50 px-4 py-3">
          <strong className="font-heading text-sm">{t("sp.lifecycle")}</strong>
          <span
            data-status={student.lifecycleStatus}
            className="ms-auto rounded-full bg-success-soft px-2.5 py-1 font-heading text-2xs font-bold text-success data-[status=withdrawn]:bg-warning-soft data-[status=withdrawn]:text-warning data-[status=graduated]:bg-sunken data-[status=graduated]:text-muted-foreground"
          >
            {t(statusKey[student.lifecycleStatus])}
          </span>
        </div>
        {editable && (
          <div className="grid gap-2 p-3">
            {student.lifecycleStatus === "active" ? (
              <>
                <button
                  type="button"
                  className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-input bg-white px-3 font-heading text-xs font-bold text-destructive"
                  onClick={() => controller.start("withdrawal")}
                >
                  <LogOut className="size-4" />
                  {t("sp.withdrawStudent")}
                </button>
                {student.lifecycle.graduationEligible && (
                  <button
                    type="button"
                    className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-input bg-white px-3 font-heading text-xs font-bold text-primary-strong"
                    onClick={() => controller.start("graduation")}
                  >
                    <GraduationCap className="size-4" />
                    {t("sp.graduateStudent")}
                  </button>
                )}
              </>
            ) : student.lifecycleStatus === "withdrawn" ? (
              <button
                type="button"
                className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-3 font-heading text-xs font-bold text-white"
                onClick={() => controller.start("return")}
              >
                <RotateCcw className="size-4" />
                {t("sp.returnStudent")}
              </button>
            ) : null}
            {student.lifecycle.events.length > 0 && (
              <button
                type="button"
                className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-input bg-white px-3 font-heading text-xs font-bold text-primary-strong"
                onClick={() => controller.start("correction")}
              >
                <PencilLine className="size-4" />
                {t("sp.correctLifecycle")}
              </button>
            )}
          </div>
        )}
        {prepaidReviewMad !== "0" && (
          <div className="flex items-center justify-between gap-3 border-t border-warning-border bg-warning-soft px-4 py-3 text-2xs text-warning">
            <strong>{t("sp.lifecyclePrepaid")}</strong>
            <strong>
              <Amount value={prepaidReviewMad} locale={locale} />
            </strong>
          </div>
        )}
        <div className="border-t border-border/50 px-4 py-3">
          <strong className="chams-eyebrow">{t("sp.lifecycleHistory")}</strong>
          {student.lifecycle.events.length ? (
            <div className="mt-2 flex flex-col gap-2">
              {student.lifecycle.events.map((event) => (
                <div key={event.id} className="rounded-xl bg-surface-2 p-3 text-2xs">
                  <span className="flex items-center justify-between gap-3">
                    <strong>
                      {t(eventKey[event.type])} · {t(statusKey[event.resultingStatus])}
                    </strong>
                    <DateValue value={event.effectiveDate} />
                  </span>
                  <p className="mt-1 leading-relaxed">{event.reason}</p>
                  <small className="mt-1 block text-muted-foreground">
                    <Bidi>{locale === "ar" ? event.actorNameAr : event.actorNameFr}</Bidi>
                  </small>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-2xs text-muted-foreground">{t("sp.lifecycleNoHistory")}</p>
          )}
        </div>
      </div>

      <MobileSurface
        opened={mode !== null}
        title={mode ? t(modeTitle[mode]) : t("sp.lifecycle")}
        closeLabel={t("common.close")}
        testId="student-lifecycle-surface"
        onClose={controller.close}
      >
        {mode && (
          <form
            className="flex flex-1 flex-col"
            onSubmit={(event) => {
              event.preventDefault();
              void controller.submit();
            }}
          >
            <Block className="!my-3 text-xs leading-relaxed text-muted-foreground">
              {t(modeHelp[mode])}
            </Block>
            <Block strong inset className="flex flex-col gap-4">
              <DatePicker
                locale={locale}
                value={controller.effectiveDate}
                min={controller.minimumDate}
                max={controller.maximumDate}
                label={t("sp.lifecycleEffectiveDate")}
                showLabel
                onChange={controller.setEffectiveDate}
              />

              {mode === "return" && (
                <>
                  <label className="flex flex-col gap-1 text-2xs font-bold">
                    {t("sp.lifecycleClass")}
                    <FilterSelect
                      value={controller.classLabel}
                      className="h-12 rounded-xl bg-white"
                      options={controller.classOptions.map((classLabel) => ({
                        value: classLabel,
                        label: classLabel,
                      }))}
                      onChange={controller.setClassLabel}
                    />
                  </label>
                  {controller.sectionOptions.length > 0 && (
                    <label className="flex flex-col gap-1 text-2xs font-bold">
                      {t("sp.section")}
                      <FilterSelect
                        value={controller.sectionLabel ?? ""}
                        className="h-12 rounded-xl bg-white"
                        options={[
                          { value: "", label: "—" },
                          ...controller.sectionOptions.map((sectionLabel) => ({
                            value: sectionLabel,
                            label: sectionLabel,
                          })),
                        ]}
                        onChange={(value) =>
                          controller.setSectionLabel(value ? (value as "A" | "B") : null)
                        }
                      />
                    </label>
                  )}
                  <div className="flex min-h-11 items-center gap-2 text-xs font-semibold">
                    <Checkbox
                      aria-label={t("sp.lifecycleTransport")}
                      checked={controller.transport}
                      onCheckedChange={controller.setTransport}
                    />
                    {t("sp.lifecycleTransport")}
                  </div>
                  <div className="flex min-h-11 items-center gap-2 text-xs font-semibold">
                    <Checkbox
                      aria-label={t("sp.lifecycleAccompaniment")}
                      checked={controller.accompaniment}
                      onCheckedChange={controller.setAccompaniment}
                    />
                    {t("sp.lifecycleAccompaniment")}
                  </div>
                </>
              )}

              {mode === "correction" && (
                <label className="flex flex-col gap-1 text-2xs font-bold">
                  {t("sp.lifecycleResultingStatus")}
                  <FilterSelect
                    value={controller.resultingStatus}
                    className="h-12 rounded-xl bg-white"
                    options={statusOptions.map((status) => ({
                      value: status,
                      label: t(statusKey[status]),
                    }))}
                    onChange={(value) =>
                      controller.setResultingStatus(value as StudentLifecycleStatusContract)
                    }
                  />
                </label>
              )}

              <label className="flex flex-col gap-1 text-2xs font-bold">
                {t("sp.lifecycleReason")}
                <textarea
                  required
                  minLength={3}
                  maxLength={500}
                  className="min-h-24 resize-y rounded-2xl border border-input bg-white px-3.5 py-2.5 text-sm font-normal text-foreground outline-none transition-colors focus:border-teal-300 focus:ring-3 focus:ring-teal-100"
                  value={controller.reason}
                  onChange={(event) => controller.setReason(event.target.value)}
                />
              </label>
            </Block>

            {(mode === "withdrawal" || mode === "graduation") && (
              <Block strong inset className="flex flex-col gap-2 text-xs">
                {controller.previewing || !controller.impact ? (
                  <span className="text-muted-foreground">{t("sp.lifecyclePreviewing")}</span>
                ) : (
                  <>
                    <span className="flex justify-between gap-3">
                      {t("sp.lifecycleRetainedDebt")}
                      <strong>
                        <Amount value={controller.impact.retainedOutstandingMad} locale={locale} />
                      </strong>
                    </span>
                    <span className="flex justify-between gap-3">
                      {t("sp.lifecycleFutureCancelled")}
                      <strong>
                        <Amount value={controller.impact.futureCancellationMad} locale={locale} />
                      </strong>
                    </span>
                    {controller.impact.prepaidFutureMad !== "0" && (
                      <span className="flex justify-between gap-3 text-warning">
                        {t("sp.lifecyclePrepaid")}
                        <strong>
                          <Amount value={controller.impact.prepaidFutureMad} locale={locale} />
                        </strong>
                      </span>
                    )}
                    {controller.impact.pendingFutureMad !== "0" && (
                      <strong className="text-destructive">{t("sp.lifecyclePendingBlock")}</strong>
                    )}
                  </>
                )}
              </Block>
            )}

            <Block className="!my-3 flex flex-col gap-3">
              <p className="rounded-xl border border-warning-border bg-warning-soft p-3 text-2xs leading-relaxed text-warning">
                {t("sp.lifecycleImmutable")}
              </p>
              <div className="flex min-h-11 items-start gap-2 text-xs font-semibold">
                <input
                  type="checkbox"
                  className="mt-0.5 size-6 shrink-0 accent-[rgb(var(--primary))]"
                  aria-label={t("sp.lifecycleConfirmation")}
                  checked={controller.confirmed}
                  onChange={(event) => controller.setConfirmed(event.target.checked)}
                />
                {t("sp.lifecycleConfirmation")}
              </div>
              {errorKey && (
                <p role="alert" className="text-2xs text-destructive">
                  {t(errorKey)}
                </p>
              )}
            </Block>
            <div className="mobile-drawer-footer">
              <button
                type="submit"
                className="min-h-12 flex-1 rounded-xl bg-primary px-4 font-heading text-xs font-bold text-white disabled:bg-disabled"
                disabled={controller.submitDisabled}
              >
                {t("sp.lifecycleSubmit")}
              </button>
            </div>
          </form>
        )}
      </MobileSurface>
    </>
  );
}
